import { Node, VariantText } from '../types/narrative';

import { WorldSpecificNode, deserializeWorldNode } from './worldStateSerializer';

/**
 * Validates a single variant text entry
 */
export function validateVariantText(variant: any): {
  isValid: boolean;
  errors: string[];
  sanitized: VariantText;
} {
  const errors: string[] = [];

  // Check if variant is an object
  if (!variant || typeof variant !== 'object') {
    return {
      isValid: false,
      errors: ['Variant must be an object'],
      sanitized: { choiceId: '', choiceText: '', variantLine: '' },
    };
  }

  // Validate and sanitize fields
  const sanitized: VariantText = {
    choiceId: '',
    choiceText: '',
    variantLine: '',
  };

  // Validate choiceId
  if (typeof variant.choiceId === 'string') {
    sanitized.choiceId = variant.choiceId.slice(0, 100); // Limit length
  } else {
    errors.push('choiceId must be a string');
  }

  // Validate choiceText
  if (typeof variant.choiceText === 'string') {
    sanitized.choiceText = variant.choiceText.slice(0, 1000); // Limit length
  } else {
    errors.push('choiceText must be a string');
  }

  // Validate variantLine
  if (typeof variant.variantLine === 'string') {
    sanitized.variantLine = variant.variantLine.slice(0, 5000); // Limit length
  } else {
    errors.push('variantLine must be a string');
  }

  return {
    isValid: errors.length === 0,
    errors,
    sanitized,
  };
}

/**
 * Validates world-specific variant content
 */
export function validateWorldVariantContent(worldContent: any): {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  sanitized: { variants: VariantText[]; placeholderPosition?: number } | null;
} {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!worldContent || typeof worldContent !== 'object') {
    return { isValid: true, errors: [], warnings: [], sanitized: null };
  }

  const sanitized: { variants: VariantText[]; placeholderPosition?: number } = {
    variants: [],
    placeholderPosition: 0,
  };

  // Validate variants array
  if (Array.isArray(worldContent.variants)) {
    worldContent.variants.forEach((variant: any, index: number) => {
      const validation = validateVariantText(variant);
      if (!validation.isValid) {
        errors.push(`Variant ${index}: ${validation.errors.join(', ')}`);
      }
      sanitized.variants.push(validation.sanitized);
    });

    // Check for expected number of variants (should match Node2 choices)
    if (sanitized.variants.length !== 5) {
      warnings.push(`Expected 5 variants for Node2 choices, found ${sanitized.variants.length}`);
    }
  } else if (worldContent.variants !== undefined) {
    errors.push('variants must be an array');
  }

  // Validate placeholderPosition
  if (typeof worldContent.placeholderPosition === 'number') {
    sanitized.placeholderPosition = Math.max(0, worldContent.placeholderPosition);
  } else if (worldContent.placeholderPosition !== undefined) {
    warnings.push('placeholderPosition should be a number, defaulting to 0');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    sanitized: errors.length === 0 ? sanitized : null,
  };
}

/**
 * Validates the entire variant content structure
 */
export function validateVariantContent(variantContent: any): {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  sanitized: WorldSpecificNode['variantContent'] | null;
} {
  if (!variantContent || typeof variantContent !== 'object') {
    return { isValid: true, errors: [], warnings: [], sanitized: null };
  }

  const errors: string[] = [];
  const warnings: string[] = [];
  const sanitized: WorldSpecificNode['variantContent'] = {};

  // Validate goodWorld
  if (variantContent.goodWorld) {
    const goodValidation = validateWorldVariantContent(variantContent.goodWorld);
    if (!goodValidation.isValid) {
      errors.push(...goodValidation.errors.map((e) => `goodWorld: ${e}`));
    }
    warnings.push(...goodValidation.warnings.map((w) => `goodWorld: ${w}`));
    if (goodValidation.sanitized) {
      sanitized.goodWorld = goodValidation.sanitized;
    }
  }

  // Validate badWorld
  if (variantContent.badWorld) {
    const badValidation = validateWorldVariantContent(variantContent.badWorld);
    if (!badValidation.isValid) {
      errors.push(...badValidation.errors.map((e) => `badWorld: ${e}`));
    }
    warnings.push(...badValidation.warnings.map((w) => `badWorld: ${w}`));
    if (badValidation.sanitized) {
      sanitized.badWorld = badValidation.sanitized;
    }
  }

  // Check if at least one world is defined
  if (!sanitized.goodWorld && !sanitized.badWorld) {
    warnings.push('No world variant content defined');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    sanitized: errors.length === 0 ? sanitized : null,
  };
}

/**
 * Validates a node with world-specific content
 */
export function validateWorldNode(node: any): {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  sanitized: Node | null;
} {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Basic node validation
  if (!node || typeof node !== 'object') {
    return {
      isValid: false,
      errors: ['Node must be an object'],
      warnings: [],
      sanitized: null,
    };
  }

  // Required fields
  if (!node.id || typeof node.id !== 'string') {
    errors.push('Node must have a valid id');
  }

  if (!node.segmentId) {
    errors.push('Node must have a segmentId');
  }

  if (!node.type || typeof node.type !== 'string') {
    errors.push('Node must have a valid type');
  }

  // Stop if basic validation fails
  if (errors.length > 0) {
    return { isValid: false, errors, warnings, sanitized: null };
  }

  // Deserialize to get a properly typed node
  let sanitized: Node;
  try {
    sanitized = deserializeWorldNode(node);
  } catch (e) {
    return {
      isValid: false,
      errors: [`Failed to deserialize node: ${e}`],
      warnings,
      sanitized: null,
    };
  }

  // Validate variant content if present
  if (node.variantContent) {
    const variantValidation = validateVariantContent(node.variantContent);
    if (!variantValidation.isValid) {
      errors.push(...variantValidation.errors);
    }
    warnings.push(...variantValidation.warnings);
    if (variantValidation.sanitized) {
      sanitized.variantContent = variantValidation.sanitized;
    }
  }

  // Validate world-specific text fields
  const worldTextFields = [
    'textBeforeGoodWorld',
    'textAfterGoodWorld',
    'textBeforeBadWorld',
    'textAfterBadWorld',
    'textMainGoodWorld',
    'textMainBadWorld',
  ];

  worldTextFields.forEach((field) => {
    if (field in node && typeof node[field] !== 'string' && node[field] !== undefined) {
      warnings.push(`${field} should be a string`);
    }
  });

  // Validate world-specific choices
  if (node.choicesGoodWorld && !Array.isArray(node.choicesGoodWorld)) {
    errors.push('choicesGoodWorld must be an array');
  }

  if (node.choicesBadWorld && !Array.isArray(node.choicesBadWorld)) {
    errors.push('choicesBadWorld must be an array');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    sanitized: errors.length === 0 ? sanitized : null,
  };
}

/**
 * Validates all nodes in a narrative state for world-specific content
 */
export function validateWorldNodes(nodes: Record<string, any>): {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  sanitizedNodes: Record<string, Node>;
} {
  const errors: string[] = [];
  const warnings: string[] = [];
  const sanitizedNodes: Record<string, Node> = {};

  Object.entries(nodes).forEach(([nodeId, node]) => {
    // Check if this is a world-specific node
    if (
      node?.variantContent ||
      node?.textBeforeGoodWorld !== undefined ||
      node?.textBeforeBadWorld !== undefined ||
      node?.choicesGoodWorld ||
      node?.choicesBadWorld
    ) {
      const validation = validateWorldNode(node);
      if (!validation.isValid) {
        errors.push(`Node ${nodeId}: ${validation.errors.join(', ')}`);
      }
      if (validation.warnings.length > 0) {
        warnings.push(`Node ${nodeId}: ${validation.warnings.join(', ')}`);
      }
      if (validation.sanitized) {
        sanitizedNodes[nodeId] = validation.sanitized;
      }
    } else {
      // Regular node, just copy
      sanitizedNodes[nodeId] = node as Node;
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    sanitizedNodes,
  };
}

/**
 * Validates narrative state with focus on world variant data
 */
export function validateNarrativeWorldState(narrativeState: any): {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  sanitized: any;
} {
  const errors: string[] = [];
  const warnings: string[] = [];
  const sanitized = { ...narrativeState };

  // Validate nodes with world-specific content
  if (narrativeState.nodes && typeof narrativeState.nodes === 'object') {
    const nodesValidation = validateWorldNodes(narrativeState.nodes);
    if (!nodesValidation.isValid) {
      errors.push(...nodesValidation.errors);
    }
    warnings.push(...nodesValidation.warnings);
    sanitized.nodes = nodesValidation.sanitizedNodes;
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    sanitized,
  };
}
